/**
 * useInterviewPolling
 *
 * Polls a refetch callback on a fixed interval while any interview in the
 * provided list is in a transitional state (backend queue actively processing).
 *
 * Transitional statuses:
 *   pending        – Celery is generating questions
 *   audio_uploaded – Celery is transcribing the recording
 *   transcribed    – Celery is running the AI analysis
 *
 * Polling stops automatically when no interview in the list has a transitional
 * status, and cleans up on unmount/navigation.
 *
 * Usage:
 *   const isPolling = useInterviewPolling(interviews, refetch)
 */

import { useEffect, useRef } from 'react'
import type { AIInterview } from '../types/interview'

// Statuses where the backend queue is actively processing.
// All other statuses are stable and do not need polling.
const TRANSITIONAL_STATUSES = new Set<string>([
  'pending',        // questions being generated by Celery
  'audio_uploaded', // audio being transcribed by Celery
  'transcribed',    // transcript being analyzed by Celery
])

export function useInterviewPolling(
  interviews: AIInterview[],
  refetch: () => void,
  intervalMs = 5000
): boolean {
  // Keep refetch stable in the interval so changes to the callback identity
  // (e.g. from useQuery) never cause a spurious interval restart.
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  const isPolling = interviews.some((i) => TRANSITIONAL_STATUSES.has(i.status))

  useEffect(() => {
    if (!isPolling) return

    const id = setInterval(() => refetchRef.current(), intervalMs)
    return () => clearInterval(id)

    // Re-run only when polling need or interval changes — NOT on every render.
  }, [isPolling, intervalMs])

  return isPolling
}
